import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Bridge auth: Clerk handles sign-in (email/password + Google). We exchange the
 * verified Clerk session token for a TrapWorld app JWT via /api/auth/clerk, then
 * the rest of the app keeps using that app JWT exactly as before. The public
 * interface (user, token, loading, isAuthenticated, logout) is unchanged.
 */
export const AuthProvider = ({ children }) => {
    const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
    const { user: clerkUser } = useUser();

    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('trapworld_token'));
    const [loading, setLoading] = useState(true);

    // Exchange the Clerk session for our app JWT whenever Clerk's state settles.
    const sync = useCallback(async () => {
        if (!isLoaded) return; // wait for Clerk to load
        if (!isSignedIn) {
            localStorage.removeItem('trapworld_token');
            setToken(null);
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const clerkToken = await getToken();
            const profile = {
                email: clerkUser?.primaryEmailAddress?.emailAddress || null,
                username: clerkUser?.username || null,
                name: clerkUser?.fullName || clerkUser?.firstName || null,
                picture: clerkUser?.imageUrl || null,
            };
            const resp = await axios.post(`${API}/auth/clerk`, profile, {
                headers: { Authorization: `Bearer ${clerkToken}` },
            });
            const { access_token, user: userData } = resp.data;
            localStorage.setItem('trapworld_token', access_token);
            setToken(access_token);
            setUser(userData);
        } catch (error) {
            console.error('Clerk auth exchange failed:', error);
            localStorage.removeItem('trapworld_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [isLoaded, isSignedIn, getToken, clerkUser]);

    useEffect(() => {
        sync();
    }, [sync]);

    // Re-pull the TrapWorld user (e.g. after a profile edit) without re-exchanging.
    const refreshUser = useCallback(async () => {
        if (!token) return;
        try {
            const resp = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(resp.data);
        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
    }, [token]);

    const logout = useCallback(async () => {
        localStorage.removeItem('trapworld_token');
        setToken(null);
        setUser(null);
        try {
            await signOut();
        } catch (error) {
            console.error('Sign out error:', error);
        }
    }, [signOut]);

    const value = {
        user,
        token,
        loading: loading || !isLoaded,
        logout,
        refreshUser,
        isAuthenticated: !!isSignedIn && !!token && !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
